import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Users, Heart, MapPin, Loader2, Sparkles } from 'lucide-react';

const TribalGenealogyForm = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    // User Information
    user_full_name: '',
    user_birth_location: '',
    
    // Maternal Side
    maternal_mother_name: '',
    maternal_mother_birth_location: '',
    maternal_grandmother_name: '',
    maternal_grandmother_birth_location: '',
    maternal_grandfather_name: '',
    maternal_grandfather_birth_location: '',
    maternal_great_grandmother_name: '',
    maternal_great_grandmother_birth_location: '',
    maternal_great_grandfather_name: '',
    maternal_great_grandfather_birth_location: '',
    
    // Paternal Side
    paternal_father_name: '',
    paternal_father_birth_location: '',
    paternal_grandmother_name: '',
    paternal_grandmother_birth_location: '',
    paternal_grandfather_name: '',
    paternal_grandfather_birth_location: '',
    paternal_great_grandmother_name: '',
    paternal_great_grandmother_birth_location: '',
    paternal_great_grandfather_name: '',
    paternal_great_grandfather_birth_location: '',
    
    // Additional Information
    known_tribal_affiliations: '',
    oral_history: ''
  });

  const tribalRegions = [
    { id: 1, name: 'Abenaki L\'Nabi', locations: ['Maine', 'New Hampshire', 'Vermont', 'Eastern Canada'] },
    { id: 2, name: 'Cherokee', locations: ['North Carolina', 'South Carolina', 'Georgia', 'Tennessee', 'Alabama', 'Oklahoma'] },
    { id: 3, name: 'Yamasee', locations: ['South Carolina', 'Georgia', 'Florida'] },
    { id: 4, name: 'Chickasaw', locations: ['Mississippi', 'Alabama', 'Tennessee', 'Kentucky', 'Oklahoma'] },
    { id: 5, name: 'Kansa (Kaw)', locations: ['Kansas', 'Oklahoma', 'Missouri'] },
    { id: 6, name: 'Choctaw', locations: ['Mississippi', 'Alabama', 'Louisiana', 'Oklahoma'] },
    { id: 7, name: 'Anasazi', locations: ['Colorado', 'Utah', 'Arizona', 'New Mexico'] },
    { id: 8, name: 'Hopi Zuni', locations: ['Arizona', 'New Mexico'] },
    { id: 9, name: 'Kānaka Maoli', locations: ['Hawaii'] },
    { id: 10, name: 'Arawak (Taíno)', locations: ['Puerto Rico', 'Dominican Republic', 'Cuba', 'Jamaica', 'Bahamas'] },
    { id: 11, name: 'Aztec', locations: ['Central Mexico', 'Mexico City region'] },
    { id: 12, name: 'Mayan', locations: ['Yucatan', 'Guatemala', 'Belize', 'Honduras', 'El Salvador'] },
    { id: 13, name: 'Wayuu', locations: ['Colombia', 'Venezuela'] },
    { id: 14, name: 'Goojijwininiwag', locations: ['Northern Ontario', 'Manitoba'] },
    { id: 15, name: 'Ojibway-Cree', locations: ['Minnesota', 'Wisconsin', 'Michigan', 'Ontario', 'Manitoba'] },
    { id: 16, name: 'Sioux Tribe', locations: ['North Dakota', 'South Dakota', 'Minnesota', 'Nebraska', 'Montana'] },
    { id: 17, name: 'Blackfoot (Niitsítapi)', locations: ['Montana', 'Alberta', 'Saskatchewan'] },
    { id: 18, name: 'The Cowichan Tribes', locations: ['British Columbia', 'Vancouver Island'] },
    { id: 19, name: 'Metis', locations: ['Manitoba', 'Saskatchewan', 'Alberta', 'Ontario', 'Northwest Territories'] },
    { id: 20, name: 'Haida', locations: ['British Columbia', 'Alaska', 'Haida Gwaii'] },
    { id: 21, name: 'Dogrib/Tåîchô', locations: ['Northwest Territories'] },
    { id: 22, name: 'Inuit', locations: ['Nunavut', 'Northwest Territories', 'Northern Quebec', 'Labrador', 'Alaska', 'Greenland'] },
    { id: 23, name: 'Algonquin', locations: ['Ontario', 'Quebec', 'Eastern Canada'] },
    { id: 24, name: 'Mi\'kmaq', locations: ['Nova Scotia', 'New Brunswick', 'Prince Edward Island', 'Newfoundland', 'Eastern Maine'] }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.user_full_name || !formData.maternal_great_grandmother_name) {
      alert('Please fill in your name and maternal great grandmother\'s name (required for tribal region analysis).');
      return;
    }

    // Determine tribal region based on maternal great grandmother's birth location
    const maternalGreatGrandmotherLocation = formData.maternal_great_grandmother_birth_location.toLowerCase();
    let tribalRegion = null;
    
    for (const region of tribalRegions) {
      const locationMatch = region.locations.some(loc => 
        maternalGreatGrandmotherLocation.includes(loc.toLowerCase()) ||
        loc.toLowerCase().includes(maternalGreatGrandmotherLocation)
      );
      if (locationMatch) {
        tribalRegion = region;
        break;
      }
    }

    const analysisData = {
      ...formData,
      analysis_type: 'tribal_genealogy',
      determined_tribal_region: tribalRegion,
      maternal_lineage_region_determination: {
        great_grandmother_location: formData.maternal_great_grandmother_birth_location,
        matched_tribal_region: tribalRegion?.name || 'Unknown',
        region_id: tribalRegion?.id || null
      }
    };

    if (onSubmit) {
      onSubmit(analysisData);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Tribal Genealogy Analysis</h2>
        <p className="text-gray-600">
          Your tribal region will be determined based on your maternal great grandmother's origin location.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* User Information */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-600" />
              Your Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="user_full_name">Your Full Name *</Label>
                <Input
                  id="user_full_name"
                  type="text"
                  value={formData.user_full_name}
                  onChange={(e) => handleInputChange('user_full_name', e.target.value)}
                  placeholder="e.g., Maria Gonzalez"
                  required
                />
              </div>
              <div>
                <Label htmlFor="user_birth_location">Your Birth Location</Label>
                <Input
                  id="user_birth_location"
                  type="text"
                  value={formData.user_birth_location}
                  onChange={(e) => handleInputChange('user_birth_location', e.target.value)}
                  placeholder="e.g., Phoenix, Arizona"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Maternal Side */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Heart className="w-5 h-5 mr-2 text-pink-600" />
              Maternal Lineage
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Mother */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Mother</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="maternal_mother_name">Mother's Full Name</Label>
                  <Input
                    id="maternal_mother_name"
                    type="text"
                    value={formData.maternal_mother_name}
                    onChange={(e) => handleInputChange('maternal_mother_name', e.target.value)}
                    placeholder="e.g., Isabella Rodriguez"
                  />
                </div>
                <div>
                  <Label htmlFor="maternal_mother_birth_location">Mother's Birth Location</Label>
                  <Input
                    id="maternal_mother_birth_location"
                    type="text"
                    value={formData.maternal_mother_birth_location}
                    onChange={(e) => handleInputChange('maternal_mother_birth_location', e.target.value)}
                    placeholder="e.g., Albuquerque, New Mexico"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Grandparents */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Maternal Grandparents</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="maternal_grandmother_name">Maternal Grandmother's Name</Label>
                  <Input
                    id="maternal_grandmother_name"
                    type="text"
                    value={formData.maternal_grandmother_name}
                    onChange={(e) => handleInputChange('maternal_grandmother_name', e.target.value)}
                    placeholder="e.g., Elena Martinez"
                  />
                </div>
                <div>
                  <Label htmlFor="maternal_grandmother_birth_location">Grandmother's Birth Location</Label>
                  <Input
                    id="maternal_grandmother_birth_location"
                    type="text"
                    value={formData.maternal_grandmother_birth_location}
                    onChange={(e) => handleInputChange('maternal_grandmother_birth_location', e.target.value)}
                    placeholder="e.g., Santa Fe, New Mexico"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label htmlFor="maternal_grandfather_name">Maternal Grandfather's Name</Label>
                  <Input
                    id="maternal_grandfather_name"
                    type="text"
                    value={formData.maternal_grandfather_name}
                    onChange={(e) => handleInputChange('maternal_grandfather_name', e.target.value)}
                    placeholder="e.g., Carlos Martinez"
                  />
                </div>
                <div>
                  <Label htmlFor="maternal_grandfather_birth_location">Grandfather's Birth Location</Label>
                  <Input
                    id="maternal_grandfather_birth_location"
                    type="text"
                    value={formData.maternal_grandfather_birth_location}
                    onChange={(e) => handleInputChange('maternal_grandfather_birth_location', e.target.value)}
                    placeholder="e.g., Taos, New Mexico"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Great Grandparents - Critical for tribal region determination */}
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                <MapPin className="w-4 h-4 mr-2 text-yellow-600" />
                Maternal Great Grandparents (Tribal Region Determination)
              </h4>
              <p className="text-sm text-yellow-800 mb-4">
                Your tribal region will be determined based on your maternal great grandmother's birth location.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="maternal_great_grandmother_name">Great Grandmother's Name *</Label>
                  <Input
                    id="maternal_great_grandmother_name"
                    type="text"
                    value={formData.maternal_great_grandmother_name}
                    onChange={(e) => handleInputChange('maternal_great_grandmother_name', e.target.value)}
                    placeholder="e.g., Rosa Luna"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="maternal_great_grandmother_birth_location">Great Grandmother's Birth Location *</Label>
                  <Input
                    id="maternal_great_grandmother_birth_location"
                    type="text"
                    value={formData.maternal_great_grandmother_birth_location}
                    onChange={(e) => handleInputChange('maternal_great_grandmother_birth_location', e.target.value)}
                    placeholder="e.g., Yucatan, Mexico"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label htmlFor="maternal_great_grandfather_name">Great Grandfather's Name</Label>
                  <Input
                    id="maternal_great_grandfather_name"
                    type="text"
                    value={formData.maternal_great_grandfather_name}
                    onChange={(e) => handleInputChange('maternal_great_grandfather_name', e.target.value)}
                    placeholder="e.g., Diego Luna"
                  />
                </div>
                <div>
                  <Label htmlFor="maternal_great_grandfather_birth_location">Great Grandfather's Birth Location</Label>
                  <Input
                    id="maternal_great_grandfather_birth_location"
                    type="text"
                    value={formData.maternal_great_grandfather_birth_location}
                    onChange={(e) => handleInputChange('maternal_great_grandfather_birth_location', e.target.value)}
                    placeholder="e.g., Merida, Yucatan"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Paternal Side */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-600" />
              Paternal Lineage
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Father */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Father</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="paternal_father_name">Father's Full Name</Label>
                  <Input
                    id="paternal_father_name"
                    type="text"
                    value={formData.paternal_father_name}
                    onChange={(e) => handleInputChange('paternal_father_name', e.target.value)}
                    placeholder="e.g., Miguel Gonzalez"
                  />
                </div>
                <div>
                  <Label htmlFor="paternal_father_birth_location">Father's Birth Location</Label>
                  <Input
                    id="paternal_father_birth_location"
                    type="text"
                    value={formData.paternal_father_birth_location}
                    onChange={(e) => handleInputChange('paternal_father_birth_location', e.target.value)}
                    placeholder="e.g., Denver, Colorado"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Grandparents */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Paternal Grandparents</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="paternal_grandmother_name">Paternal Grandmother's Name</Label>
                  <Input
                    id="paternal_grandmother_name"
                    type="text"
                    value={formData.paternal_grandmother_name}
                    onChange={(e) => handleInputChange('paternal_grandmother_name', e.target.value)}
                    placeholder="e.g., Carmen Valdez"
                  />
                </div>
                <div>
                  <Label htmlFor="paternal_grandmother_birth_location">Grandmother's Birth Location</Label>
                  <Input
                    id="paternal_grandmother_birth_location"
                    type="text"
                    value={formData.paternal_grandmother_birth_location}
                    onChange={(e) => handleInputChange('paternal_grandmother_birth_location', e.target.value)}
                    placeholder="e.g., Pueblo, Colorado"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label htmlFor="paternal_grandfather_name">Paternal Grandfather's Name</Label>
                  <Input
                    id="paternal_grandfather_name"
                    type="text"
                    value={formData.paternal_grandfather_name}
                    onChange={(e) => handleInputChange('paternal_grandfather_name', e.target.value)}
                    placeholder="e.g., Juan Gonzalez"
                  />
                </div>
                <div>
                  <Label htmlFor="paternal_grandfather_birth_location">Grandfather's Birth Location</Label>
                  <Input
                    id="paternal_grandfather_birth_location"
                    type="text"
                    value={formData.paternal_grandfather_birth_location}
                    onChange={(e) => handleInputChange('paternal_grandfather_birth_location', e.target.value)}
                    placeholder="e.g., Durango, Colorado"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Great Grandparents */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Paternal Great Grandparents</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="paternal_great_grandmother_name">Great Grandmother's Name</Label>
                  <Input
                    id="paternal_great_grandmother_name"
                    type="text"
                    value={formData.paternal_great_grandmother_name}
                    onChange={(e) => handleInputChange('paternal_great_grandmother_name', e.target.value)}
                    placeholder="e.g., Maria Santos"
                  />
                </div>
                <div>
                  <Label htmlFor="paternal_great_grandmother_birth_location">Great Grandmother's Birth Location</Label>
                  <Input
                    id="paternal_great_grandmother_birth_location"
                    type="text"
                    value={formData.paternal_great_grandmother_birth_location}
                    onChange={(e) => handleInputChange('paternal_great_grandmother_birth_location', e.target.value)}
                    placeholder="e.g., Mesa Verde, Colorado"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label htmlFor="paternal_great_grandfather_name">Great Grandfather's Name</Label>
                  <Input
                    id="paternal_great_grandfather_name"
                    type="text"
                    value={formData.paternal_great_grandfather_name}
                    onChange={(e) => handleInputChange('paternal_great_grandfather_name', e.target.value)}
                    placeholder="e.g., Antonio Santos"
                  />
                </div>
                <div>
                  <Label htmlFor="paternal_great_grandfather_birth_location">Great Grandfather's Birth Location</Label>
                  <Input
                    id="paternal_great_grandfather_birth_location"
                    type="text"
                    value={formData.paternal_great_grandfather_birth_location}
                    onChange={(e) => handleInputChange('paternal_great_grandfather_birth_location', e.target.value)}
                    placeholder="e.g., Cortez, Colorado"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
              Additional Heritage Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="known_tribal_affiliations">Known Tribal Affiliations</Label>
              <Input
                id="known_tribal_affiliations"
                type="text"
                value={formData.known_tribal_affiliations}
                onChange={(e) => handleInputChange('known_tribal_affiliations', e.target.value)}
                placeholder="e.g., Mayan, Anasazi, Cherokee"
              />
            </div>
            
            <div>
              <Label htmlFor="oral_history">Family Stories & Oral History</Label>
              <textarea
                id="oral_history"
                className="w-full p-3 border border-gray-300 rounded-md"
                rows={4}
                value={formData.oral_history}
                onChange={(e) => handleInputChange('oral_history', e.target.value)}
                placeholder="Share any family stories, traditions, or oral history you've heard..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 shadow-lg">
          <CardContent className="pt-6">
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing Tribal Analysis...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-5 w-5" />
                  Begin Tribal Heritage Analysis
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default TribalGenealogyForm;